import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { Modal } from '../../../components/Modal/Modal'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { eventTypeEditorPath } from './bookingNavigation'
import { EventTypeNotLiveDialog } from './EventTypeNotLiveDialog'
import { AvailabilityMode, type EventConfigInput, EventTypeAccess, MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAccountSchedulingSettingsQuery, useGetSchedulingLocationsQuery } from '../../../store/scheduling/scheduling'
import { PlaceToggleCards } from '../flows/form/PlaceToggleCards'
import { DURATION_OPTIONS, eventTypeLocationPatch, toBookingSlug } from './eventTypeOptions'
import { EventTypeLocationField } from './EventTypeLocationField'
import { RedtailActivityMatchFields } from './RedtailActivityMatchFields'
import { isPartialRedtailSelection, REDTAIL_UNSET, toRedtailEventConfig } from './eventConfig'
import { useRedtailEntityOptions, useRedtailEventTypeAccess } from './useRedtailEventConfig'
import styles from './EventTypeModal.module.scss'

interface EventTypeCreateModalProps {
  access: EventTypeAccess
  isCreating: boolean
  accountBookingEnabled?: boolean
  onClose: () => void
  onCreate: (eventType: SchedulingEventType, eventConfig?: EventConfigInput) => Promise<SchedulingEventType | null>
}

type CreateStep = 'form' | 'not-live'

const ACCOUNT_PAGE_LOCKED_HINT = 'Turn on the account booking page in Setup to use this.'

interface PlacementCheckboxProps {
  id: string
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  disabledHint?: string
  onChange: (checked: boolean) => void
}

const PlacementCheckbox: FC<PlacementCheckboxProps> = ({ id, label, description, checked, disabled = false, disabledHint, onChange }) => (
  <Tooltip title={disabled ? disabledHint : undefined}>
    <div className='d-flex align-items-center gap-3 p-3'>
      <Form.Check
        type='checkbox'
        className='mb-0'
        id={id}
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <label htmlFor={id} className='d-flex flex-column'>
        <span className='bold'>{label}</span>
        <span className='paragraph-small zocks-text-gray-600'>{description}</span>
      </label>
    </div>
  </Tooltip>
)

const COPY: Record<EventTypeAccess, { title: string; subtitle: string }> = {
  [EventTypeAccess.ACCOUNT]: {
    title: 'Create shared event type',
    subtitle: 'Visible to everyone in your account. Uses account defaults you can change later.',
  },
  [EventTypeAccess.PERSONAL]: {
    title: 'Create personal event type',
    subtitle: 'Only you can offer this event type. Uses account defaults you can change later.',
  },
}

export const EventTypeCreateModal: FC<EventTypeCreateModalProps> = ({ access, isCreating, accountBookingEnabled = false, onClose, onCreate }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const availableLocations = locations ?? []
  const defaultLocation = availableLocations.find((location) => location.id === accountSettings?.defaults.locationId)

  const redtailAccess = useRedtailEventTypeAccess(accountId, access)
  const { activityCodeOptions, categoryOptions } = useRedtailEntityOptions(accountId, redtailAccess.enabled)

  const [name, setName] = useState('')
  const [durationMinutes, setDurationMinutes] = useState(30)
  const [meetingMode, setMeetingMode] = useState<MeetingMode>(MeetingMode.VIRTUAL)
  const [locationValue, setLocationValue] = useState('')
  const isShared = access === EventTypeAccess.ACCOUNT
  // A shared event type starts off both pages; a personal one is on its owner's page right away.
  const [isActive, setIsActive] = useState(!isShared)
  const [isOnAccountPage, setIsOnAccountPage] = useState(false)
  const [activityCodeId, setActivityCodeId] = useState(REDTAIL_UNSET)
  const [categoryId, setCategoryId] = useState(REDTAIL_UNSET)
  const [step, setStep] = useState<CreateStep>('form')
  const [createdEventType, setCreatedEventType] = useState<SchedulingEventType | null>(null)

  const isInPerson = meetingMode === MeetingMode.IN_PERSON

  const handleCreate = async () => {
    const entry = redtailAccess.enabled ? toRedtailEventConfig(activityCodeId, categoryId) : null
    const created = await onCreate(
      {
        id: '',
        name: name.trim(),
        slug: toBookingSlug(name),
        durationMinutes,
        meetingMode,
        clientFacingText: '',
        active: isActive,
        accountPageVisible: isShared && isOnAccountPage,
        access,
        location: isInPerson ? eventTypeLocationPatch(locationValue).location : null,
        availabilityMode: AvailabilityMode.ACCOUNT_DEFAULT,
        bufferBeforeMinutes: 0,
        bufferAfterMinutes: 0,
        minimumNoticeMinutes: 1440,
        bookingWindowDays: 60,
        addToUserPagesByDefault: false,
        usedByCount: 0,
        updatedAt: '',
      },
      entry ? { redtail: entry } : undefined,
    )
    if (!created) {
      return
    }
    if (created.accountPageVisible) {
      setCreatedEventType(created)
      setStep('not-live')
      return
    }
    onClose()
  }

  const handleContinueToHosts = () => {
    if (createdEventType) {
      navigate(eventTypeEditorPath(createdEventType.id, 'booking-pages'), { state: { origin: location.pathname } })
    }
    onClose()
  }

  const copy = COPY[access]

  if (step === 'not-live' && createdEventType) {
    return <EventTypeNotLiveDialog onContinue={handleContinueToHosts} onCancel={onClose} />
  }

  return (
    <Modal
      show
      dialogClassName={styles.dialog}
      onHide={onClose}
      header={
        <div className='d-flex align-items-start zocks-gap-0.5'>
          {isShared ? <i className='bi bi-people-fill zocks-text-gray-900 mt-1' aria-hidden='true' /> : null}
          <div className='d-flex flex-column zocks-gap-0.25'>
            <h5 className='mb-0'>{copy.title}</h5>
            <span className='paragraph-small zocks-text-gray-600'>{copy.subtitle}</span>
          </div>
        </div>
      }
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='event-type-name'>
              Name <span className='zocks-text-danger'>*</span>
            </label>
            <span className='paragraph-small zocks-text-gray-600'>Clients see this when they book.</span>
            <Form.Control
              id='event-type-name'
              required
              type='text'
              value={name}
              placeholder='e.g. Annual review'
              onChange={(event) => setName(event.currentTarget.value)}
            />
          </div>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='event-type-duration'>
              Duration
            </label>
            <Form.Select id='event-type-duration' value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.currentTarget.value))}>
              {DURATION_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes} minutes
                </option>
              ))}
            </Form.Select>
          </div>
          <PlaceToggleCards
            meetingMode={meetingMode}
            defaultLocation={defaultLocation}
            onChange={(mode, locId) => {
              setMeetingMode(mode)
              setLocationValue(locId ?? '')
            }}
          />
          {isInPerson ? (
            <EventTypeLocationField id='event-type-location' locations={availableLocations} value={locationValue} onChange={setLocationValue} />
          ) : null}
          {isShared ? (
            <div className='d-flex flex-column zocks-gap-0.25'>
              <span className='bold'>Where clients can book this</span>
              <div className='d-flex flex-column border rounded-3 zocks-border-color-gray-200'>
                <div className='border-bottom zocks-border-color-gray-200'>
                  <PlacementCheckbox
                    id='event-type-on-account-page'
                    label='Account booking page'
                    description='Bookings go to the hosts you assign.'
                    checked={isOnAccountPage}
                    disabled={!accountBookingEnabled}
                    disabledHint={ACCOUNT_PAGE_LOCKED_HINT}
                    onChange={setIsOnAccountPage}
                  />
                </div>
                <PlacementCheckbox
                  id='event-type-on-individual-pages'
                  label='Individual booking pages'
                  description='Each person can choose whether to use this event type.'
                  checked={isActive}
                  onChange={setIsActive}
                />
              </div>
            </div>
          ) : (
            <div className='d-flex align-items-start justify-content-between gap-3'>
              <div className='d-flex flex-column'>
                <span className='bold'>Individual booking page</span>
                <span className='paragraph-small zocks-text-gray-600'>Clients can book this on your page.</span>
              </div>
              <Form.Switch checked={isActive} aria-label='Individual booking page' onChange={(event) => setIsActive(event.currentTarget.checked)} />
            </div>
          )}
          {redtailAccess.enabled ? (
            <RedtailActivityMatchFields
              idPrefix='event-type'
              activityCodeId={activityCodeId}
              categoryId={categoryId}
              activityCodeOptions={activityCodeOptions}
              categoryOptions={categoryOptions}
              onActivityCodeChange={setActivityCodeId}
              onCategoryChange={setCategoryId}
            />
          ) : null}
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle='Create event type'
      footerSecondaryActionTitle='Cancel'
      footerPrimaryActionDisabled={
        !name.trim() || (isInPerson && !locationValue) || (redtailAccess.enabled && isPartialRedtailSelection(activityCodeId, categoryId))
      }
      footerPrimaryActionLoading={isCreating}
      onFooterPrimaryAction={() => void handleCreate()}
      onFooterSecondaryAction={onClose}
    />
  )
}
